<template>
    <Dialog v-model:open="isOpen">
        <DialogTrigger as-child>
            <slot />
        </DialogTrigger>
        <DialogContent class="max-w-md">
            <DialogHeader>
                <template #icon>
                    <QrCode
                        class="text-z-primary-600"
                        stroke-width="2.25"
                        :size="20"
                    />
                </template>
                <DialogTitle>
                    {{ $t("event_types.qr_code.title") }}
                </DialogTitle>
                <DialogDescription>
                    {{ $t("event_types.qr_code.description") }}
                </DialogDescription>
            </DialogHeader>

            <div class="gap-4 px-6 pb-6 flex flex-col items-center">
                <div
                    v-if="loading"
                    class="py-12 flex items-center justify-center"
                >
                    <div
                        class="h-8 w-8 animate-spin border-z-primary-200 border-t-z-primary-600 rounded-full border-4"
                    ></div>
                </div>

                <div v-else-if="error" class="text-red-600 text-center">
                    {{ $t("event_types.qr_code.error") }}
                </div>

                <template v-else-if="qrCodeData">
                    <div class="mt-4 rounded-lg bg-white p-4">
                        <img
                            :src="qrCodeData"
                            alt="QR Code"
                            class="h-64 w-64"
                        />
                    </div>

                    <div class="gap-2 flex w-full">
                        <Button
                            @click="downloadQrCode"
                            class="flex-1"
                            variant="outline"
                        >
                            <Download :size="16" class="mr-2" />
                            {{ $t("event_types.qr_code.download") }}
                        </Button>
                    </div>
                </template>
            </div>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { QrCode, Copy, Download } from "lucide-vue-next";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog-v2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import route from "ziggy-js";

interface Props {
    eventTypeId: number;
    open?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    open: undefined,
});

const emit = defineEmits<{
    (e: "update:open", value: boolean): void;
}>();

const isOpen = computed({
    get: () => props.open ?? internalOpen.value,
    set: (value) => {
        if (props.open !== undefined) {
            emit("update:open", value);
        } else {
            internalOpen.value = value;
        }
    },
});

const internalOpen = ref(false);
const loading = ref(false);
const error = ref(false);
const qrCodeData = ref<string | null>(null);
const eventUrl = ref<string>("");
const copied = ref(false);

watch(isOpen, async (newValue) => {
    if (newValue && !qrCodeData.value) {
        await fetchQrCode();
    }
});

async function fetchQrCode() {
    loading.value = true;
    error.value = false;

    try {
        const response = await fetch(
            route("app.event-types.qr-code.show", {
                eventType: props.eventTypeId,
            }),
        );

        if (!response.ok) {
            throw new Error("Failed to fetch QR code");
        }

        const data = await response.json();
        qrCodeData.value = data.qrCode;
        eventUrl.value = data.url;
    } catch (e) {
        error.value = true;
        console.error("Error fetching QR code:", e);
    } finally {
        loading.value = false;
    }
}

async function copyUrl() {
    try {
        await navigator.clipboard.writeText(eventUrl.value);
        copied.value = true;
        setTimeout(() => {
            copied.value = false;
        }, 2000);
    } catch (e) {
        console.error("Failed to copy URL:", e);
    }
}

function downloadQrCode() {
    if (!qrCodeData.value) return;

    const link = document.createElement("a");
    link.href = qrCodeData.value;
    link.download = `qr-code-event-type-${props.eventTypeId}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
</script>
